import type {
  IAssetTemplateStatus,
  ITemplateUsageDetail,
  IDisableReasonEntry,
  IAssetTemplateModalData,
  IUsedTemplateSuffixes,
} from '../types';
import type { ICampaignTemplate } from '~/modules/campaign-template/types';
import type { AssetTemplate } from '~/modules/shopify';

import { getExistingCampaigns, getUsedPageIds } from '~/modules/campaign-group-template/helpers';
import { IAssetTemplateModalType, IDisableReasonKey } from '../types';
import { isExistingPageAssigned } from '~/modules/campaign-template';
import { PROVIDER_SOURCE_GEMPAGES } from '~/constants/template';
import { ProviderSource, TemplateType } from '~/types/template';
import { findVariantByType } from '~/modules/campaign/utils';
import { IVariantType } from '~/modules/campaign/types';
import { DATA_TEMPLATE_DISABLED } from '../constants';
import { parseTemplateInfoBy } from '~/utils';

export * from './asset-template';
export * from './templateUsage';
export * from './tooltip';

/**
 * Checks if the template is currently selected
 * @param templateKey - The key of the template
 * @param currentTemplateSuffix - The currently selected template suffix
 * @returns The disable reason key if selected, otherwise null
 */
const checkIfSelected = (
  templateKey: string,
  currentTemplateSuffix?: string,
): IDisableReasonKey | null => {
  return currentTemplateSuffix === templateKey ? IDisableReasonKey.IS_SELECTED : null;
};

/**
 * Checks the usage of the template in campaigns
 * @param usageDetails - The usage details of the template
 * @returns The disable reason key based on usage, otherwise null
 */
const checkTemplateUsage = (
  usageDetails: ITemplateUsageDetail[] | undefined,
): IDisableReasonKey | null => {
  if (!usageDetails) return null;

  const hasOtherCampaignGroup = usageDetails.some((detail) => detail.isOtherGroupCampaign);
  if (hasOtherCampaignGroup) {
    return IDisableReasonKey.IS_USED_FOR_OTHER_CAMPAIGN_GROUP;
  }

  // A price test occupies the template through the product it runs on and a URL test through the page
  // it sends traffic to, so both lock the suffix the same way a single template experiment does.
  const hasOtherSingleCampaign = usageDetails.some(
    (detail) =>
      detail.isOtherSingerCampaign || detail.isOtherPriceCampaign || detail.isOtherUrlCampaign,
  );
  if (hasOtherSingleCampaign) {
    return IDisableReasonKey.IS_USED_FOR_OTHER_CAMPAIGN;
  }

  const hasCurrentCampaignControl = usageDetails.some(
    (detail) => detail.currentCampaignType === IVariantType.VARIANT_A,
  );
  const hasCurrentCampaignGroupControl = usageDetails.some(
    (detail) => detail.currentCampaignGroupType === IVariantType.VARIANT_A,
  );
  if (hasCurrentCampaignGroupControl || hasCurrentCampaignControl) {
    return IDisableReasonKey.IS_USED_FOR_CONTROL;
  }
  const hasCurrentCampaignVariant = usageDetails.some(
    (detail) => detail.currentCampaignType && detail.currentCampaignType !== IVariantType.VARIANT_A,
  );
  const hasCurrentCampaignGroupVariant = usageDetails.some(
    (detail) =>
      detail.currentCampaignGroupType && detail.currentCampaignGroupType !== IVariantType.VARIANT_A,
  );
  if (hasCurrentCampaignGroupVariant || hasCurrentCampaignVariant) {
    return IDisableReasonKey.IS_USED_FOR_VARIANT;
  }

  return null;
};

/**
 * Checks if the template has assigned pages
 * @param template - The template to check
 * @param isHomePage - Whether the template is a homepage
 * @returns The disable reason key if no pages are assigned, otherwise null
 */
export const checkAssignmentStatus = ({
  template,
  modalData,
  modalType,
}: {
  template: AssetTemplate;
  modalType?: IAssetTemplateModalType;
  modalData?: IAssetTemplateModalData;
}): IDisableReasonKey | null => {
  const templateInfo = parseTemplateInfoBy({ templateSuffix: template.key });
  const isHomePage = templateInfo?.type === TemplateType.HOME;
  if (isHomePage && template.providerSource === ProviderSource.SHOPIFY_ORIGIN) return null;

  const isPickVariantB = modalType === IAssetTemplateModalType.VARIANT_B;
  if (isPickVariantB) {
    const variantA = findVariantByType(modalData?.campaign, IVariantType.VARIANT_A);
    const isTemplateSamePageType =
      parseTemplateInfoBy({ templateSuffix: template.key })?.type === variantA?.templateType;
    if (isTemplateSamePageType) return null;
  }

  const isPickVariantA = modalType === IAssetTemplateModalType.VARIANT_A;
  if (isPickVariantA) {
    const variantB = findVariantByType(modalData?.campaign, IVariantType.VARIANT_B);
    const templateType = parseTemplateInfoBy({ templateSuffix: template.key })?.type;
    const isTemplateSamePageType =
      !variantB?.templateType || variantB.templateType === templateType;
    const isNotSupport = !isTemplateSamePageType && !variantB?.template?.redirectURL;
    if (isNotSupport) return IDisableReasonKey.IS_TEMPLATE_VARIANT_NOT_ASSIGNED;
  }

  const hasPagesAssigned = isExistingPageAssigned(template);
  if (hasPagesAssigned) return null;

  const providerSource = template.themePageInfo?.providerSource;
  if (!providerSource) return null;

  if (PROVIDER_SOURCE_GEMPAGES.includes(providerSource)) {
    return IDisableReasonKey.IS_NO_ASSIGNED_BY_GEMPAGES;
  }

  return IDisableReasonKey.IS_NO_ASSIGNED_BY_SHOPIFY;
};

/**
 * Checks if the template is hidden or in draft state
 * @param template - The template to check
 * @returns The disable reason key if hidden, otherwise null
 */
const checkHiddenStatus = (template: AssetTemplate): IDisableReasonKey | null => {
  return template.themePageInfo?.isHide ? IDisableReasonKey.IS_TEMPLATE_DRAFT : null;
};

/**
 * Checks if the template is used in a test campaign
 * @param isHomePage - Whether the template is a homepage
 * @param isUsedByOtherCampaigns - Whether the template is used by other campaigns
 * @returns The disable reason key if used in a test campaign, otherwise null
 */
const checkTestCampaignUsage = (
  isHomePage: boolean,
  isUsedByOtherCampaigns: boolean,
): IDisableReasonKey | null => {
  if (isHomePage) return null;
  if (!isUsedByOtherCampaigns) return null;

  return IDisableReasonKey.IS_TEMPLATE_USED_TO_TEST;
};

/**
 * Determines if a template is disabled and returns the reason
 * @param params - Parameters for checking the disabled state
 * @returns The disable reason key or undefined if not disabled
 */
export const getStateDisable = ({
  modalType,
  modalData,
  template,
  templateStatus,
  usedTemplateSuffixes,
  currentTemplateSuffix,
}: {
  modalType?: IAssetTemplateModalType;
  modalData?: IAssetTemplateModalData;
  template: AssetTemplate;
  templateStatus: IAssetTemplateStatus;
  usedTemplateSuffixes: IUsedTemplateSuffixes;
  currentTemplateSuffix?: string;
}): IDisableReasonKey | undefined => {
  // Priority 1: Check if the template is currently selected
  const isSelected = checkIfSelected(template.key, currentTemplateSuffix);
  if (isSelected) return isSelected;

  // Priority 2: Check if the template is used in another campaign
  const usageReason = checkTemplateUsage(usedTemplateSuffixes[template.key]);
  if (usageReason) return usageReason;

  // Priority 3: Check if the template has assigned pages
  const assignmentReason = checkAssignmentStatus({
    template,
    modalData,
    modalType,
  });
  if (assignmentReason) return assignmentReason;

  // Priority 4: Check if the template is hidden
  const hiddenReason = checkHiddenStatus(template);
  if (hiddenReason) return hiddenReason;

  // Priority 5: Check if the template is used in a test campaign
  const testCampaignReason = checkTestCampaignUsage(
    templateStatus?.isHomePage,
    templateStatus.isUsedByOtherCampaigns,
  );
  if (testCampaignReason) return testCampaignReason;

  return undefined;
};

/**
 * Filters templates that do not have assigned pages
 * @param template - The template to check
 * @param modalPick - The current modal pick state
 * @returns Whether the template is valid (has assigned pages)
 */
export const filterTemplateHasAssigned = (
  template: AssetTemplate,
  modalType?: IAssetTemplateModalType,
): boolean => {
  if (!modalType || modalType === IAssetTemplateModalType.VARIANT_B) return true; // If modalPick is undefined, assume the template is valid
  const hasPagesAssigned = isExistingPageAssigned(template);
  return hasPagesAssigned;
};

/**
 * Finds a disable reason entry by key
 * @param key - The disable reason key to find
 * @returns The matching disable reason entry or undefined if not found
 */
export const findDisableReasonEntryBy = (
  key: IDisableReasonKey,
): IDisableReasonEntry | undefined => {
  return DATA_TEMPLATE_DISABLED.find((item) => item.key === key);
};

export const checkTemplateNoSpecifiedPageAvailable = ({
  asset,
  campaigns,
}: {
  asset: AssetTemplate;
  campaigns?: ICampaignTemplate[];
}) => {
  const existingCampaigns = getExistingCampaigns(asset.key || '', campaigns || []);
  const usedPageIds = getUsedPageIds(existingCampaigns);
  const totalUsedPageIds = usedPageIds.length;
  const isUsedByOtherCampaigns = existingCampaigns.length > 0;
  const assignedPageCount = asset.assigned?.total ?? 0;
  const isDisabled =
    isUsedByOtherCampaigns && (!totalUsedPageIds || totalUsedPageIds === assignedPageCount);

  return isDisabled;
};
